/**
 * Pure helpers for the upcharge calculator commit logic in the reseller plan
 * form.  Extracted so they can be unit-tested independently of the React
 * component tree.
 *
 * Each function receives the raw string values from the calculator inputs and
 * returns:
 *   - `hint`            – non-empty when the inputs are incomplete/invalid
 *   - `upchargeInCents` – the computed value to write to the plan form
 *                         (absent when the inputs are invalid)
 */

export interface CalcAiResult {
  hint: string;
  upchargeInCents?: number;
}

export interface CalcStorageResult {
  hint: string;
}

/**
 * Validate & compute the AI platform-cost calculator.
 *
 * @param cost    Raw string from "Your platform AI cost / month ($)" input
 * @param markup  Raw string from the AI markup (%) input
 */
export function calcAiCommit(cost: string, markup: string): CalcAiResult {
  const costNum = parseFloat(cost);
  const markupNum = parseFloat(markup);
  if (cost === "" || isNaN(costNum) || markup === "" || isNaN(markupNum)) {
    return { hint: "Fill in both fields to apply the AI upcharge." };
  }
  return {
    hint: "",
    upchargeInCents: Math.round(costNum * (1 + markupNum / 100) * 100),
  };
}

/**
 * Validate & compute the media-storage calculator.
 *
 * @param cost    Raw string from "Your platform media cost / month ($)" input
 * @param markup  Raw string from the media markup (%) input
 */
export function calcMediaCommit(cost: string, markup: string): CalcAiResult {
  const costNum = parseFloat(cost);
  const markupNum = parseFloat(markup);
  if (cost === "" || isNaN(costNum) || markup === "" || isNaN(markupNum)) {
    return { hint: "Fill in both fields to apply the media upcharge." };
  }
  return {
    hint: "",
    upchargeInCents: Math.round(costNum * (1 + markupNum / 100) * 100),
  };
}

/**
 * Validate the file-storage calculator (display-only – no form value is
 * written for storage; the upcharge is auto-derived from storageLimitMb).
 *
 * @param costPerGb  Raw string from "Your cost ($/GB/month)" input
 * @param markup     Raw string from the storage markup (%) input
 */
export function calcStorageCommit(
  costPerGb: string,
  markup: string
): CalcStorageResult {
  const costNum = parseFloat(costPerGb);
  const markupNum = parseFloat(markup);
  if (
    costPerGb === "" ||
    isNaN(costNum) ||
    markup === "" ||
    isNaN(markupNum)
  ) {
    return { hint: "Fill in both fields to see the storage rate." };
  }
  return { hint: "" };
}
